import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { hasSectionAccess } from '@/rbac/console-sections'

export function setupRouterGuards(router: Router) {
  router.beforeEach(async (to, _from, next) => {
    const authStore = useAuthStore()

    document.title = to.meta.title
      ? `${to.meta.title} - PEACE Console`
      : 'PEACE Console'

    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    // Coarse door check: must hold a Console primary role (site_admin/site_owner).
    if (to.meta.roles && to.meta.roles.length > 0) {
      const hasRole = to.meta.roles.some(role =>
        authStore.userRoles.includes(role)
      )
      if (!hasRole) {
        next({ name: 'forbidden' })
        return
      }
    }

    // U3 (§7.3.27): deep-link section permission. Every gated Console page carries
    // a `meta.section` (stamped in router/index.ts from the single-source
    // SECTION_ROUTE_TABLE). If the caller's platform roles do not grant that
    // section, send them to the EXPLICIT /403 forbidden page — never silently
    // render the page (whose data calls the backend would 403 anyway) and never
    // silently redirect the failed route elsewhere. This mirrors the backend
    // authority `auth.require_section(ConsoleSection::…)`. Rollback keeps this
    // deny path + the visible forbidden page intact.
    const section = to.meta.section
    if (section) {
      const allowed = hasSectionAccess(
        authStore.platformRoles,
        section,
        authStore.user?.role,
      )
      if (!allowed) {
        next({
          name: 'forbidden',
          query: { section, denied: to.fullPath },
        })
        return
      }
    }

    next()
  })
}
